import { useContext } from 'react';
import { logRejectionsToSentry } from '../../Sentry';
import { useRouter } from '../../hooks/useRouter';
import { Fetch, useFetch } from '../../services/NetworkService';
import { useEventBus } from '../../util/EventBus';
import { AnalyticsContext } from '../AnalyticsContext';
import { TrackingEvent } from '../TrackingEvent';

export const PracticeAdminAnalyticsDispatcher = () => {
  const { bus } = useContext(AnalyticsContext);
  const fetch = useFetch();
  const router = useRouter();
  const path = router?.asPath ?? '';

  useEventBus(bus, ({ name, data }) => {
    const { searchData, resultsData, practiceData, semData, socialData } = data;
    const appearanceTracking = resultsData?.appearanceTracking ?? {};
    const practiceId = practiceData?.grpLocId;
    const businessId = semData?.businessId;

    const {
      practices,
      practitioners,
      practiceNearbyAppointments,
      practiceStillLooking,
    } = appearanceTracking;

    switch (name) {
      case TrackingEvent.SEARCH_PERFORMED: {
        if (practices && practices.length > 0) {
          if (searchData?.filtersApplied?.onlineAppts) {
            track(fetch, 'appeared/search-appointment', { ref: path, ids: practices });
          }

          track(fetch, 'appeared/search-practice', { ref: path, ids: practices });
        }

        if (practitioners && practitioners.length > 0) {
          track(fetch, 'appeared/search-practitioner', { ref: path, ids: practitioners });
        }

        break;
      }

      case TrackingEvent.PROFILE_PAGE_LOADED: {
        if (practiceId) {
          track(fetch, 'viewed/practice', { ref: practiceId });

          track(fetch, 'event', {
            id: practiceId,
            page: 'Direct Link',
            element: 'Profile Page Link',
            event: 'click',
            url: path,
          });
        }

        break;
      }

      case TrackingEvent.PROFILE_PRACTICE_LINK_CLICKED: {
        if (practiceId) {
          track(fetch, 'event', {
            id: practiceId,
            page: 'Practice',
            element: 'Practice Link',
            event: 'click',
            url: path,
          });
        }

        break;
      }

      case TrackingEvent.PROFILE_SOCIAL_LINK_CLICKED: {
        if (practiceId) {
          track(fetch, 'event', {
            id: practiceId,
            page: 'Practice',
            element: `Social Link (${socialData?.socialName})`,
            event: 'click',
            url: path,
          });
        }

        break;
      }

      case TrackingEvent.PROFILE_NEARBY_APPOINTMENTS_LOADED: {
        if (practiceId && practiceNearbyAppointments) {
          track(fetch, 'appeared/practice-nearby-appointments', {
            ref: practiceId,
            ids: practiceNearbyAppointments,
          });
        }

        break;
      }

      case TrackingEvent.PROFILE_STILL_LOOKING_LOADED: {
        if (practiceId && practiceStillLooking) {
          track(fetch, 'appeared/practice-still-looking', {
            ref: practiceId,
            ids: practiceStillLooking,
          });
        }

        break;
      }

      case TrackingEvent.PROFILE_PHONE_CLICKED: {
        if (practiceId) {
          track(fetch, 'event', {
            id: practiceId,
            page: 'Practice',
            element: 'Practice - Phone Number Reveal',
            event: 'click',
          });
        }

        break;
      }

      case TrackingEvent.PROFILE_ENTER_BUSINESS_ID: {
        if (practiceId && businessId) {
          track(fetch, 'event', {
            id: practiceId,
            page: 'Practice',
            element: 'Gopher business_id',
            reference: businessId,
            url: path,
          });
        }

        break;
      }
    }
  });

  return null;
};

type TrackingPath =
  | 'appeared/practice-nearby-appointments'
  | 'appeared/practice-still-looking'
  | 'appeared/search-practice'
  | 'appeared/search-practitioner'
  | 'appeared/search-appointment'
  | 'event'
  | 'viewed/practice';

const track = (fetch: Fetch, eventName: TrackingPath, body: Record<string, string>) => {
  logRejectionsToSentry(`Tracking prac-admin event ${eventName}`, () =>
    postUrlEncodedData(fetch, `/api/1/track/${eventName}`, body),
  );
};

const postUrlEncodedData = (fetch: Fetch, path: string, body: Record<string, string>) =>
  fetch(path, {
    method: 'POST',
    body: new URLSearchParams(body),
  });
